import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackModelRecord, GetFeedbacksResponse } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { VoteHandler } from "./useVoteSubmission";

const HANDLER_DEBOUNCE_TIMEOUT = 300;

export interface UseAiForMeFeedbacksReturn {
  /** State containing all user feedbacks keyed by taxonomyId */
  userFeedbacks: Record<string, FeedbackModelRecord>;
  /** Ready-to-use voteHandler to pass to AiForMeItem */
  voteHandler: VoteHandler;
  /** Loading state for initial feedbacks fetch */
  isLoadingFeedbacks: boolean;
  /** Direct setter for userFeedbacks (for edge cases or external updates) */
  setUserFeedbacks: Dispatch<SetStateAction<Record<string, FeedbackModelRecord>>>;
}

/**
 * Custom hook to manage user feedbacks at the list/page level for AI for Me tools.
 *
 * This hook encapsulates:
 * 1. Loading all user feedbacks on mount via `getMyFeedbacks()`
 * 2. Creating a debounced `voteHandler` that can be passed to `AiForMeItem` components
 * 3. Updating the feedbacks state after a successful vote
 *
 * @param client - CatalogicalService instance for the "aiforme" domain
 * @param csrfService - CSRFService instance for obtaining CSRF tokens
 * @returns Object containing userFeedbacks state, voteHandler, loading state, and setter
 *
 * @example
 * ```tsx
 * const client = useMemo(() => new CatalogicalService<AIForMeTool>("aiforme"), []);
 * const csrfService = useMemo(() => new CSRFService(), []);
 *
 * const { userFeedbacks, voteHandler, isLoadingFeedbacks } = useAiForMeFeedbacks(client, csrfService);
 *
 * // Pass to items:
 * <AiForMeItem
 *   voteHandler={voteHandler}
 *   userVote={userFeedbacks[item.taxonomyId]}
 *   // ...
 * />
 * ```
 */
export const useAiForMeFeedbacks = (
  client: CatalogicalService<AIForMeTool>,
  csrfService: CSRFService
): UseAiForMeFeedbacksReturn => {
  const [userFeedbacks, setUserFeedbacks] = useState<Record<string, FeedbackModelRecord>>({});
  const [isLoadingFeedbacks, setIsLoadingFeedbacks] = useState(true);

  // Refs for stable voteHandler identity - avoids recreating voteHandler on every feedback change
  const userFeedbacksRef = useRef(userFeedbacks);
  userFeedbacksRef.current = userFeedbacks;

  // Ref to track the debounce timeout
  const voteTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Load user's existing feedbacks on component mount
  useEffect(() => {
    let isMounted = true;

    const loadFeedbacks = async () => {
      setIsLoadingFeedbacks(true);
      try {
        const myFeedbackResponse: GetFeedbacksResponse | undefined = await client.getMyFeedbacks();

        if (!isMounted) return;

        if (myFeedbackResponse && myFeedbackResponse.data) {
          const feedbackMap: Record<string, FeedbackModelRecord> = {};
          myFeedbackResponse.data.forEach((feedback) => {
            if (feedback && feedback.taxonomyId) {
              feedbackMap[feedback.taxonomyId] = feedback;
            }
          });
          setUserFeedbacks(feedbackMap);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching user feedbacks:", error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingFeedbacks(false);
        }
      }
    };

    loadFeedbacks();

    return () => {
      isMounted = false;
    };
  }, [client]);

  /**
   * Handles submitting a feedback vote. Uses a timeout to prevent multiple submits (debouncing).
   * @param taxId The taxonomy id of the tool
   * @param vote The vote value (rating)
   * @returns Promise resolving to true if successful, false otherwise
   */
  const voteHandler: VoteHandler = useCallback(
    async (taxId: TaxonomyIdItem, vote: number): Promise<boolean> => {
      const token = await csrfService.getToken();

      const voteHandlerPromise = new Promise<boolean>((resolve, reject) => {
        if (voteTimeoutRef.current) {
          clearTimeout(voteTimeoutRef.current);
          reject("debounce");
        }

        voteTimeoutRef.current = setTimeout(async () => {
          client
            .postFeedback(taxId, { rating: vote }, token)
            .then((response: FeedbackModelRecord) => {
              // Update the userFeedbacks state with the new vote using ref for latest value
              setUserFeedbacks({
                ...userFeedbacksRef.current,
                [taxId.toString()]: response,
              });
              resolve(true);
            })
            .catch((reason) => {
              console.error("Vote submission failed:", reason);
              resolve(false);
            })
            .finally(() => {
              voteTimeoutRef.current = undefined;
            });
        }, HANDLER_DEBOUNCE_TIMEOUT);
      });

      return voteHandlerPromise;
    },
    [client, csrfService]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (voteTimeoutRef.current) {
        clearTimeout(voteTimeoutRef.current);
      }
    };
  }, []);

  return {
    userFeedbacks,
    voteHandler,
    isLoadingFeedbacks,
    setUserFeedbacks,
  };
};
